import { Dispatch, Store } from 'types/Store';
import { UserThunks } from 'modules/user/userActions';
import { AccountThunks } from 'modules/account/accountActions';
import { createAction } from 'modules/helpers';
import { MODULE_STATUS } from 'constants/modules';
import { ListingsThunks } from 'modules/listings/listingsActions';
import { PageThunk } from 'modules/page/pageActions';
import FacebookUtils from 'app/utils/facebook';
import Logger from 'utils/logger';
import i18n from 'app/utils/i18n';
import {
  initializeThirdPartApplicationsProperties,
  loadThirdPartyScriptsAfterAuth,
} from 'app/utils/initializeThirdPartApplicationsProperties';
import { setUserTimePreference } from 'app/utils/setUserTimePreference';
import { G2ReviewTracker } from 'app/utils/g2ReviewTracker';
import { showToast } from 'app/hooks/useToast';
import { useAppLayoutState } from 'components/appLayout/appLayoutState';
import CognitoTokenManager from 'app/modules/auth/utils/cognitoTokenManager';
import { BrowserStorage } from 'utils/browserStorage';
import { TokenRefreshService } from 'app/modules/auth/utils/tokenRefreshService';
import { isTokenExpired } from 'app/modules/auth/utils/jwtUtils';
import { getExternalLoginUrl } from 'app/modules/auth/utils/externalAuthUrls';

export const AppActionNames = {
  SET_APP_INITIALIZED: 'AppActionNames-SET_APP_INITIALIZED',
  SET_AUTH_TOKEN: 'AppActionNames-SET_AUTH_TOKEN',
  SET_APP_UNAUTHORIZED: 'AppActionNames-SET_APP_UNAUTHORIZED',
};

export const AppActions = {
  setAppInitialized: createAction(AppActionNames.SET_APP_INITIALIZED),
  setAppUnAuthorized: createAction(AppActionNames.SET_APP_UNAUTHORIZED),
  setAuthToken: createAction<string | null>(AppActionNames.SET_AUTH_TOKEN),
};

const appLayout = useAppLayoutState.getState();

export const AppThunks = {
  initializeApp: () => async (dispatch: Dispatch, getState: () => Store) => {
    try {
      Logger.log('[Auth][Init] initializeApp started');
      appLayout.hideBackdropLoader();
      let authToken = await CognitoTokenManager.getAccessToken();
      Logger.log(
        `[Auth][Init] getAccessToken result: ${
          authToken ? `present (${authToken.length} chars)` : 'null'
        }`,
      );

      if (!authToken || isTokenExpired(authToken)) {
        Logger.log(
          `[Auth][Init] Token ${
            !authToken ? 'missing' : 'expired'
          }, checking for refresh token...`,
        );
        const tokens = CognitoTokenManager.getTokens();

        if (tokens?.refreshToken) {
          Logger.log('[Auth][Init] Refresh token found, attempting refresh...');
          try {
            const refreshed = await TokenRefreshService.refresh();
            authToken = refreshed.access_token;
            Logger.log(
              `[Auth][Init] Token refresh succeeded, new token: ${authToken.length} chars`,
            );
          } catch (refreshErr) {
            Logger.error(
              '[Auth][Init] Token refresh failed:',
              (refreshErr as Error)?.message,
            );
            authToken = null;
          }
        } else {
          Logger.warn('[Auth][Init] No refresh token available');
        }
      }

      if (!authToken) {
        Logger.error(
          '[Auth][Init] No valid auth token, aborting initialization',
        );
        throw new Error('No valid Cognito session');
      }

      Logger.log(
        '[Auth][Init] Setting auth token in Redux and starting proactive refresh',
      );
      await dispatch(AppActions.setAuthToken(authToken));
      TokenRefreshService.initializeProactiveRefresh();

      Logger.log(
        '[Auth][Init] Fetching user, permissions, account, listings...',
      );
      await Promise.all([
        dispatch(UserThunks.requestUser()),
        dispatch(UserThunks.loadUserPermissions()),
        dispatch(AccountThunks.requestAccount()),
        dispatch(ListingsThunks.requestListings()),
      ]);

      const { user, account, listings } = getState();
      Logger.log(
        `[Auth][Init] Init data statuses: user=${user.status}, account=${account.status}, listings=${listings.status}`,
      );

      const hasReceivedInitData = [
        user.status,
        account.status,
        listings.status,
      ].every((status) => status === MODULE_STATUS.Succeeded);

      if (hasReceivedInitData) {
        loadThirdPartyScriptsAfterAuth();
        G2ReviewTracker.resetPostSaveCount();
        initializeThirdPartApplicationsProperties({ account, user });
        FacebookUtils.init();
        setUserTimePreference(user.useMeridianTime);

        dispatch(PageThunk.requestPages());
        dispatch(AppActions.setAppInitialized());
        Logger.log('[Auth][Init] App initialized successfully');
        return;
      }

      throw new Error('Unable to Authorize User');
    } catch (e: any) {
      Logger.error(`[Auth][Init] initializeApp FAILED: ${e?.message}`);
      dispatch(AppActions.setAppUnAuthorized());
      Logger.warn('Cannot initialize application:', e);
      showToast(i18n.t('unableToAuthorizeLogin'), 'warning');
    }
  },
  logoutUser: () => async (dispatch: Dispatch) => {
    try {
      appLayout.showBackdropLoader();
      TokenRefreshService.cleanup();
      CognitoTokenManager.signOut();
      BrowserStorage.logoutClear();
      await dispatch(AppActions.setAuthToken(null));
      G2ReviewTracker.resetPostSaveCount();
    } catch (e) {
      Logger.error(
        'Logout failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    } finally {
      appLayout.hideBackdropLoader();
      window.location.href = getExternalLoginUrl();
    }
  },
};
